import React, { useState, useEffect, useRef } from "react";
import {
    AppBar,
    Badge,
    Toolbar,
    Typography,
    Box,
    Drawer,
    IconButton,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    CssBaseline,
} from "@mui/material";
import {
    Menu as MenuIcon,
    VideoLibrary as VideoLibraryIcon,
    Article as ArticleIcon,
    People as PeopleIcon,
    ExitToApp as ExitToAppIcon,
    Storage as StorageIcon,
    Quiz as ExamIcon
} from "@mui/icons-material";
import CloudQueueIcon from "@mui/icons-material/CloudQueue";
import { Link } from "react-router-dom";
import {
    signOut,
} from "firebase/auth";
import { useNavigate } from "react-router-dom";
import { auth } from "../../firebaseConfig";
import Greeting from "../../common/Greeting";

const drawerWidth = 240;
const primaryColor = "#9c27b0";

const CommonNavbar = () => {
    const [open, setOpen] = useState(false);
    const drawerRef = useRef();
    const toggleButtonRef = useRef(null);
    const logoutButtonRef = useRef(null);
    const studensDataCount = localStorage.getItem("studentDataCount");
    const hrDataCount = localStorage.getItem("hrDataCount");
    const hrCallsCount = localStorage.getItem("hrCallsCount");

    const navigate = useNavigate();

    const user = JSON.parse(localStorage.getItem("user"));

    const handleLogout = async () => {
        try {
            await signOut(auth); // Sign out from Firebase
            localStorage.removeItem("user");
            localStorage.removeItem("adminEmail");
            localStorage.removeItem("adminPassword");
            navigate("/login"); // Redirect to login
        } catch (error) {
            console.error("Logout failed: ", error);
        }
    };

    useEffect(() => {
        const handleClickOutside = (event) => {
          const clickedOutsideDrawer = drawerRef.current && !drawerRef.current.contains(event.target);
          const clickedOutsideToggle = toggleButtonRef.current && !toggleButtonRef.current.contains(event.target);
          const clickedOutsideLogout = logoutButtonRef.current && !logoutButtonRef.current.contains(event.target);
    
          if (open && clickedOutsideDrawer && clickedOutsideToggle && clickedOutsideLogout) {
            setOpen(false);
        }
        };
    
        document.addEventListener('mousedown', handleClickOutside);
    
        return () => {
          document.removeEventListener('mousedown', handleClickOutside);
        };
      }, [open, setOpen]);

    const drawerItemStyle = {
        color: 'grey',
        '&:hover': {
            backgroundColor: 'rgba(156, 39, 176, 0.1)',
        },
    };

    const drawerIconStyle = { color: primaryColor };

    return (
        <>
            <CssBaseline />
            <AppBar
                position="fixed"
                color="default"
                elevation={3}
                sx={{
                    zIndex: (theme) => theme.zIndex.drawer + 1,
                    background: "linear-gradient(to right, #457b9d, #6a1b9a)",
                    color: "#fff",
                }}
            >
                <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
                    <Box sx={{ display: "flex", alignItems: "center" }}>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="menu"
                            ref={toggleButtonRef}
                            onClick={() => setOpen(!open)}
                            sx={{ mr: 2 }}
                        >
                            <MenuIcon />
                        </IconButton>
                        <Typography
                            component={Link}
                            to="/"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                textDecoration: "none",
                                fontWeight: "bold",
                                fontSize: "1.8rem",
                                fontFamily: "'Pacifico', 'Roboto', sans-serif",
                                letterSpacing: 1,
                                textShadow: "2px 2px 5px rgba(0,0,0,0.2)",
                                background: "linear-gradient(45deg, #ffcc00, #ff33cc)",
                                WebkitBackgroundClip: "text",
                                WebkitTextFillColor: "transparent",
                            }}
                        >
                            <CloudQueueIcon sx={{ fontSize: 40, mr: 1 }} />
                            Cloud UpSkill
                        </Typography>
                    </Box>

                    <Box display="flex" alignItems="center" gap={2}>
                        <Greeting user={user} />
                    </Box>
                </Toolbar>
            </AppBar>

            <Drawer
                variant="persistent"
                anchor="left"
                open={open}
                sx={{
                    width: drawerWidth,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: drawerWidth,
                        boxSizing: "border-box",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        backgroundColor: "snow"
                    },
                }}
            >
                <Box ref={drawerRef} sx={{ flexGrow: 1 }}>
                    <Toolbar />
                    {user.role === 'admin' ? (
                        <List>
                            <ListItem button component={Link} to="/admin/student-data" sx={drawerItemStyle} onClick={() => setOpen(false)}>
                                <ListItemIcon sx={drawerIconStyle}><PeopleIcon /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <span>Students</span>
                                            <Badge
                                                badgeContent={studensDataCount}
                                                color="primary"
                                                sx={{ "& .MuiBadge-badge": { right: 10 } }}
                                                showZero
                                                max={999}
                                            />
                                        </Box>
                                    }
                                />
                            </ListItem>
                            <ListItem button component={Link} to="/admin/hr-data" sx={drawerItemStyle} onClick={() => setOpen(false)}>
                                <ListItemIcon sx={drawerIconStyle}><StorageIcon /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <span>HR Data</span>
                                            <Badge
                                                badgeContent={hrDataCount}
                                                color="primary"
                                                sx={{ "& .MuiBadge-badge": { right: 10 } }}
                                                showZero
                                                max={999}
                                            />
                                        </Box>
                                    }
                                />
                            </ListItem>

                            <Divider sx={{ my: 1 }} />

                            <ListItem button component={Link} to="/admin/videos" sx={drawerItemStyle} onClick={() => setOpen(false)}>
                                <ListItemIcon sx={drawerIconStyle}><VideoLibraryIcon /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <span>Videos</span>
                                        </Box>
                                    }
                                />
                            </ListItem>
                            <ListItem button component={Link} to="/admin/materials" sx={drawerItemStyle} onClick={() => setOpen(false)}>
                                <ListItemIcon sx={drawerIconStyle}><ArticleIcon /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <span>Materials</span>
                                        </Box>
                                    }
                                />
                            </ListItem>
                            <ListItem button component={Link} to="/admin/assignments" sx={drawerItemStyle} onClick={() => setOpen(false)}>
                                <ListItemIcon sx={drawerIconStyle}><ExamIcon /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <span>Assignments</span>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        </List>) :
                        <List>
                            <ListItem button component={Link} to="/student/hr-data" sx={drawerItemStyle} onClick={() => setOpen(false)}>
                                <ListItemIcon sx={drawerIconStyle}><StorageIcon /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <span>HR Data</span>
                                            <Badge
                                                badgeContent={hrCallsCount}
                                                color="primary"
                                                sx={{ "& .MuiBadge-badge": { right: 10 } }}
                                                showZero
                                                max={999}
                                            />
                                        </Box>
                                    }
                                />
                            </ListItem>

                            <Divider sx={{ my: 1 }} />

                            <ListItem button component={Link} to="/student/videos" sx={drawerItemStyle} onClick={() => setOpen(false)}>
                                <ListItemIcon sx={drawerIconStyle}><VideoLibraryIcon /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <span>Videos</span>
                                        </Box>
                                    }
                                />
                            </ListItem>
                            <ListItem button component={Link} to="/student/materials" sx={drawerItemStyle} onClick={() => setOpen(false)}>
                                <ListItemIcon sx={drawerIconStyle}><ArticleIcon /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <span>Materials</span>
                                        </Box>
                                    }
                                />
                            </ListItem>
                            <ListItem button component={Link} to="/student/assignments" sx={drawerItemStyle} onClick={() => setOpen(false)}>
                                <ListItemIcon sx={drawerIconStyle}><ExamIcon /></ListItemIcon>
                                <ListItemText
                                    primary={
                                        <Box display="flex" justifyContent="space-between" alignItems="center">
                                            <span>Assignments</span>
                                        </Box>
                                    }
                                />
                            </ListItem>
                        </List>}
                </Box>

                <Box>
                    <Divider />
                    <List>
                        <ListItem
                            button
                            ref={logoutButtonRef}
                            onClick={handleLogout}
                            sx={{
                                color: "#616161",
                                '&:hover': {
                                    backgroundColor: 'rgba(229, 57, 53, 0.1)',
                                    color: '#e53935',
                                    cursor: 'pointer'
                                }
                            }}
                        >
                            <ListItemIcon sx={{ color: "#616161" }}><ExitToAppIcon /></ListItemIcon>
                            <ListItemText primary="Logout" />
                        </ListItem>
                    </List>
                </Box>
            </Drawer>

            <Box component="main" sx={{ flexGrow: 1, p: 1, ml: open ? `${drawerWidth}px` : 0 }}>
                <Toolbar />
                {/* Your main content */}
            </Box>
        </>
    );
};

export default CommonNavbar;
